import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import RecommendationsSection from "@/components/RecommendationsSection";
import VisitDetailDialog from "@/components/VisitDetailDialog";
import { 
  Star, Trophy, MapPin, Utensils, Plus, LogOut, 
  User, Settings, Award, TrendingUp, Calendar,
  ChefHat, Menu, X, Bell, Users, BarChart3, Map, Heart, Check, Target, Sparkles
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const BADGE_COLORS = {
  "Food Novice": "badge-novice",
  "Food Explorer": "badge-explorer",
  "Food Connoisseur": "badge-connoisseur",
  "Prince Foody": "badge-prince",
  "Royal Foody": "badge-royal"
};

const BADGE_THRESHOLDS = [
  { name: "Food Novice", threshold: 0 },
  { name: "Food Explorer", threshold: 5 },
  { name: "Food Connoisseur", threshold: 15 },
  { name: "Prince Foody", threshold: 30 },
  { name: "Royal Foody", threshold: 50 }
];

const Dashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedVisit, setSelectedVisit] = useState(null);

  // Use user from location state if available (from auth callback)
  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (location.state?.user && !user) {
      checkAuth();
    }
  }, [location.state, user, checkAuth]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, visitsRes, notifRes, countRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`, { withCredentials: true }),
        axios.get(`${API}/visits`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setVisits(Array.isArray(visitsRes.data) ? visitsRes.data.slice(0, 5) : []);
      const notifArray = Array.isArray(notifRes.data) ? notifRes.data : (notifRes.data?.notifications || []);
      setNotifications(notifArray.slice(0, 5));
      setUnreadCount(countRes.data?.count || 0);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      } else {
        console.warn("Error fetching dashboard data:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.warn("Failed to mark notifications as read:", error.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getProgressToNextBadge = () => {
    if (!stats) return { progress: 0, current: 0, next: 5 };
    
    const currentStars = stats.total_stars;
    let currentThreshold = 0;
    let nextThreshold = 5;
    
    for (let i = 0; i < BADGE_THRESHOLDS.length; i++) {
      if (currentStars >= BADGE_THRESHOLDS[i].threshold) {
        currentThreshold = BADGE_THRESHOLDS[i].threshold;
        nextThreshold = BADGE_THRESHOLDS[i + 1]?.threshold || currentThreshold;
      }
    }
    
    if (currentThreshold === nextThreshold) {
      return { progress: 100, current: currentStars, next: nextThreshold };
    }
    
    const progress = ((currentStars - currentThreshold) / (nextThreshold - currentThreshold)) * 100;
    return { progress: Math.min(progress, 100), current: currentStars, next: nextThreshold };
  };

  const progressData = getProgressToNextBadge();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/dashboard')} className="menu-item text-sm text-foreground py-2">
                {t('common.dashboard')}
              </button>
              <button onClick={() => navigate('/restaurants')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">
                {t('common.restaurants')}
              </button>
              <button onClick={() => navigate('/preferences')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">
                {t('common.preferences')}
              </button>
              <button onClick={() => navigate('/community')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">
                {t('common.community')}
              </button>
              <button onClick={() => navigate('/leaderboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">
                {t('common.leaderboard')}
              </button>
            </div>

            {/* User Menu */}
            <div className="hidden md:flex items-center gap-4">
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative" data-testid="notifications-button">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-popover border-border" align="end">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold">{t('dashboard.notifications')}</h4>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-primary">
                        {t('dashboard.markAllRead')}
                      </Button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">{t('dashboard.noNotifications')}</p>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className={`p-3 rounded ${notif.read ? 'bg-muted/10' : 'bg-primary/10'}`}
                        >
                          <p className="text-sm font-medium">{notif.title}</p>
                          <p className="text-xs text-muted-foreground">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10" data-testid="user-menu-button">
                    {currentUser?.picture ? (
                      <img src={currentUser.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="text-sm">{currentUser?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded bg-primary/20 text-primary font-medium">
                  {t('common.dashboard')}
                </button>
                <button onClick={() => { navigate('/restaurants'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded text-foreground hover:bg-muted/50">
                  {t('common.restaurants')}
                </button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded text-foreground hover:bg-muted/50">
                  {t('common.preferences')}
                </button>
                <button onClick={() => { navigate('/community'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded text-foreground hover:bg-muted/50">
                  {t('common.community')}
                </button>
                <button onClick={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded text-foreground hover:bg-muted/50">
                  {t('common.leaderboard')}
                </button>
                <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="text-left py-2 px-3 rounded text-foreground hover:bg-muted/50">
                  {t('common.profile')}
                </button>
                <button onClick={handleLogout} className="text-left py-2 px-3 rounded text-destructive hover:bg-destructive/10">
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header - Elegant */}
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground/70 mb-2 font-light">{t('dashboard.welcomeBack')}</p>
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight">
            <span className="gradient-text font-medium">{currentUser?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-sm text-muted-foreground/60 mt-1 font-light italic">{t('dashboard.journeyContinues')}</p>
        </div>

        {/* Stats Grid - Refined (3 cards) */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-10">
          {/* Total Stars */}
          <Card 
            className="bg-card/50 border-border/60 backdrop-blur-sm cursor-pointer transition-all duration-300 hover:border-primary/20 hover:bg-card/70" 
            data-testid="total-stars-card"
            onClick={() => navigate('/leaderboard')}
          >
            <CardContent className="p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-primary fill-primary mb-1 sm:mb-0" />
                <span className="text-[8px] sm:text-xs uppercase tracking-wide sm:tracking-[0.2em] text-muted-foreground font-medium">{t('dashboard.totalStars')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-extralight tracking-tight gradient-text">{stats?.total_stars || 0}</p>
            </CardContent>
          </Card>

          {/* Current Badge */}
          <Card 
            className="bg-card/50 border-border/60 backdrop-blur-sm cursor-pointer transition-all duration-300 hover:border-primary/20 hover:bg-card/70" 
            data-testid="current-badge-card"
            onClick={() => navigate('/achievements')}
          >
            <CardContent className="p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-0 ${BADGE_COLORS[stats?.badge_tier || 'Food Novice']}`} />
                <span className="text-[8px] sm:text-xs uppercase tracking-wide sm:tracking-[0.2em] text-muted-foreground font-medium">{t('dashboard.badge')}</span>
              </div>
              <p className={`text-base sm:text-lg font-light tracking-tight ${BADGE_COLORS[stats?.badge_tier || 'Food Novice']}`}>
                {stats?.badge_tier || 'Food Novice'}
              </p>
            </CardContent>
          </Card>

          {/* Visited Restaurants - Combined card */}
          <Card 
            className="bg-card/50 border-border/60 backdrop-blur-sm cursor-pointer transition-all duration-300 hover:border-primary/20 hover:bg-card/70" 
            data-testid="visited-restaurants-card"
            onClick={() => navigate('/restaurants?filter=visited')}
          >
            <CardContent className="p-3 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-1 mb-1 sm:mb-0">
                  <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <span className="text-[8px] sm:text-xs uppercase tracking-wide sm:tracking-[0.2em] text-muted-foreground font-medium">{t('dashboard.uniqueRestaurants')}</span>
              </div>
              <p className="text-2xl sm:text-3xl font-extralight tracking-tight">{stats?.unique_restaurants || 0}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{stats?.total_visits || 0} {t('dashboard.visits')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Progress and Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 space-y-6">
          {/* Progress to Next Badge */}
          <Card className="bg-card border-border" data-testid="progress-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {t('dashboard.progressTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className={BADGE_COLORS[stats?.badge_tier || 'Food Novice']}>
                    {stats?.badge_tier || 'Food Novice'}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="progress-stars-count">
                    <Star className="w-3 h-3 text-primary fill-primary" />
                    {progressData.current} / {progressData.next}
                  </span>
                  {stats?.next_badge && (
                    <span className={BADGE_COLORS[stats.next_badge]}>
                      {stats.next_badge}
                    </span>
                  )}
                </div>
                <Progress value={progressData.progress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center">
                  {stats?.next_badge ? (
                    <>
                      <span className="text-primary font-semibold">{stats.stars_to_next}</span> {t('dashboard.starsTo')} {stats.next_badge}
                    </>
                  ) : (
                    <span className="text-primary">{t('dashboard.maxBadge')}</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stars by Tier & Recent Visits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-card border-border" data-testid="stars-by-tier-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary" />
                  {t('dashboard.starsByTier')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[1, 2, 3].map((tier) => (
                    <div key={`tier-${tier}`} className="flex items-center gap-4">
                      <div className="flex gap-1">
                        {[...Array(tier)].map((_, i) => (
                          <Star key={`tier-star-${tier}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                        ))}
                        {[...Array(3 - tier)].map((_, i) => (
                          <Star key={`tier-empty-${tier}-${i}`} className="w-4 h-4 text-muted" />
                        ))}
                      </div>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ 
                            width: `${((stats?.stars_by_tier?.[tier] || 0) / Math.max(stats?.total_stars || 1, 1)) * 100}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold w-8 text-right">
                        {stats?.stars_by_tier?.[tier] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Visits */}
            <Card className="bg-card border-border" data-testid="recent-visits-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  {t('dashboard.recentVisits')}
                </CardTitle>
                {visits.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => navigate('/restaurants?filter=visited')} className="text-primary">
                    {t('common.viewAll')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <div className="text-center py-8">
                    <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">{t('dashboard.noVisits')}</p>
                    <Button 
                      onClick={() => navigate('/restaurants')}
                      className="bg-primary/10 hover:bg-primary/20 text-primary"
                    >
                      {t('dashboard.addFirstVisit')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visits.map((visit) => (
                      <div 
                        key={visit.visit_id}
                        onClick={() => setSelectedVisit(visit)}
                        className="flex items-center justify-between p-3 bg-muted/20 rounded cursor-pointer hover:bg-muted/40 transition-colors"
                        data-testid={`recent-visit-${visit.visit_id}`}
                      >
                        <div>
                          <p className="font-medium">{visit.restaurant_name}</p>
                          <p className="text-xs text-muted-foreground">{visit.visit_date}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(visit.stars)].map((_, i) => (
                            <Star key={`visit-star-${visit.visit_id}-${i}`} className="w-3 h-3 text-primary fill-primary" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          </div>

          {/* Quick Actions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={() => navigate('/restaurants')}
                className="w-full justify-start h-12 bg-primary/10 hover:bg-primary/20 text-primary"
                data-testid="add-visit-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('dashboard.addVisit')}
              </Button>
              <Button 
                onClick={() => navigate('/preferences')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="update-preferences-button"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('dashboard.updatePreferences')}
              </Button>
              <Button 
                onClick={() => navigate('/leaderboard')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-leaderboard-button"
              >
                <Award className="w-4 h-4 mr-2" />
                {t('dashboard.viewLeaderboard')}
              </Button>
              <Button 
                onClick={() => navigate('/analytics')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-analytics-button"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {t('analytics.title')}
              </Button>
              <Button 
                onClick={() => navigate('/map')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-map-button"
              >
                <Map className="w-4 h-4 mr-2" />
                {t('map.title')}
              </Button>
              <Button 
                onClick={() => navigate('/wishlist')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-wishlist-button"
              >
                <Heart className="w-4 h-4 mr-2" />
                {t('wishlist.title')}
              </Button>
              <Button 
                onClick={() => navigate('/achievements')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-achievements-button"
              >
                <Trophy className="w-4 h-4 mr-2" />
                {t('achievements.title')}
              </Button>
              <Button 
                onClick={() => navigate('/goals')}
                variant="outline"
                className="w-full justify-start h-12 border-border hover:border-primary hover:text-primary"
                data-testid="view-goals-button"
              >
                <Target className="w-4 h-4 mr-2" />
                Meine Ziele
              </Button>
              <Button 
                onClick={() => navigate('/wrapped')}
                variant="outline"
                className="w-full justify-start h-12 border-primary/50 hover:bg-primary/10 text-primary"
                data-testid="view-wrapped-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Jahresrückblick {new Date().getFullYear()}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recommendations Section */}
        <div className="mb-8">
          <RecommendationsSection />
        </div>
      </main>

      <VisitDetailDialog
        open={!!selectedVisit}
        onOpenChange={(isOpen) => { if (!isOpen) setSelectedVisit(null); }}
        visit={selectedVisit}
        onUpdated={fetchData}
      />
    </div>
  );
};

export default Dashboard;
